import {Injectable} from '@angular/core';
import * as duckdb from '@duckdb/duckdb-wasm';
import {ReplaySubject, combineLatest, take} from "rxjs";
import {
  NetscopeApiService,
  Success,
  Failure
} from "@app/services/netscope-api.service";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {
  DuckDbAppsGroup,
  DuckDbDashboardData,
  DuckDbExternalIp,
  DuckDbResourceObject, FlowRessourceObject, comboboxSelectionWhereClause
} from "@app/netscope/model/data-models";
import { v4 as uuid } from 'uuid';
import {DuckDBBundles} from "@duckdb/duckdb-wasm";
import * as pako from 'pako';
import {FlowsRepositoryWithBrowserDuckDB, FlowsRepositoryWithRemoteDuckDB} from "@app/netscope/model/flows-repository";

class DatabaseAndNodemapping {
  db?: duckdb.AsyncDuckDB;
  nodeMapping: Map<string, DuckDbResourceObject>;
  externalIps: Array<DuckDbExternalIp>;
  appsGroups: Array<DuckDbAppsGroup>;
  selectedTimestamps: [number, number];
  timeselectionLabel: string;
  uuid: string;
  tmp_parquet_filepath?: string;
  aggregated_tmp_parquet_filepath?: string;
}


export interface DuckdbServiceInterface {
  recomputeDashboardData(): void;
  requestLoadingOfData(timePeriod: string, startTime: number, endTime: number): Promise<void>;
}


@Injectable({
  providedIn: 'root'
})
export class DuckDbServiceWithInBrowserDuckDb implements DuckdbServiceInterface {

  private db!: duckdb.AsyncDuckDB;

  public dbSubject: ReplaySubject<DatabaseAndNodemapping> = new ReplaySubject(1);
  public dashboardDataSubject: ReplaySubject<DuckDbDashboardData> = new ReplaySubject(1);
  public lastUiRequestUuid:string = "";

  private isUsingCdn = true;

  public currentWorkers: Worker[] = new Array();

  constructor(private netscopeApiService: NetscopeApiService, private netscopeUiService: NetscopeUiService) {
  }

  public async requestLoadingOfData(timePeriod, startTime=0, endTime=0): Promise<void> {

    if (timePeriod === undefined) {
      timePeriod = "last 1day"
    }

    await navigator.locks.request('duckdb_lock', async () => {
      await this.stopCurrentWorkers();
      await this.initializeWorker();
      await this.reloadData(timePeriod, startTime, endTime);
    });
  }

  private async stopCurrentWorkers(): Promise<void> {
    for (let worker of this.currentWorkers) {
      console.log(`Terminating worker ${worker}`)
      worker.terminate();
    }
  }

  private async initializeWorker(useCdn=true): Promise<void> {
    const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();

    const baseUrl = window.location.origin + '/assets/duckdb-wasm/dist/';
    const LOCALS_BUNDLES: DuckDBBundles = {
      mvp: {
        mainModule: new URL(baseUrl + 'duckdb-mvp.wasm').toString(),
        mainWorker: new URL(baseUrl + 'duckdb-browser-mvp.worker.js').toString(),
      },
      eh : {
        mainModule: new URL(baseUrl + 'duckdb-eh.wasm').toString(),
        mainWorker: new URL(baseUrl + 'duckdb-browser-eh.worker.js').toString(),
      },
      coi: {
        mainModule: new URL(baseUrl + 'duckdb-coi.wasm').toString(),
        mainWorker: new URL(baseUrl + 'duckdb-browser-coi.worker.js').toString(),
        pthreadWorker: new URL(baseUrl + 'duckdb-browser-coi.pthread.worker.js').toString()
      }
    }

    let SELECTED_BUNDLES = useCdn ? JSDELIVR_BUNDLES : LOCALS_BUNDLES;

    const bundle: duckdb.DuckDBBundle = await duckdb.selectBundle(
      SELECTED_BUNDLES
    );

    let blob = new Blob([`importScripts("${bundle.mainWorker}");`], {
      type: 'text/javascript',
    });

    // Create a worker from the selected bundle
    const worker_url = URL.createObjectURL(
      blob
    );

    // Test if CDN is accessible
    if (useCdn) {
      let error = false;
      try {
        // Signal is used to add a timeout for offline browsers. I use 10 seconds
        let response = await fetch(bundle.mainWorker, { method: "GET", signal: AbortSignal.timeout(10 * 1000) });
        if (response.status !== 200) {
          console.log(`There was an issue fetching duckdb binary from cdn, I will switch to local asset loading`);
          error = true;
        }
      } catch (e) {
        error = true;
      }

      if (error) {
        console.warn("The CDN does not seem to work, we will fallback to the locally hosted dependencies");
        return this.initializeWorker(false);
      }
    }

    this.isUsingCdn = useCdn;

    const worker = new Worker(worker_url);
    this.currentWorkers.push(worker);

    const logger = new duckdb.ConsoleLogger();
    this.db = new duckdb.AsyncDuckDB(logger, worker);
    await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker);

    URL.revokeObjectURL(worker_url);

    const root = await navigator.storage.getDirectory();
    // @ts-ignore
    let entries = Object.entries(root.entries());
    console.log(`entries: ${entries}`);

    let i = 0;
    let continue_opening_files = true
    const id = window.crypto.randomUUID()
    while (i < 20 && continue_opening_files) {
      let dbName = `opfs_file_${id}_${i}.db`;

      try {
        await this.db.open({
          path: `opfs://${dbName}`,
          accessMode: duckdb.DuckDBAccessMode.READ_WRITE,
          query: {castBigIntToDouble: true, castDecimalToDouble: true},
        });
        console.warn(`File ${dbName} has been acquired, I will continue`);
        continue_opening_files = false;
      } catch (e) {
        console.warn(`There was an issue with file ${dbName}, I caught the error and the processing may continue.`);
      }

      i += 1;
    }
  }

  private async fetchData(timePeriod, startTime=0, endTime=0) {
    let payload = {
      timePeriod: timePeriod,
      startTime: startTime,
      endTime: endTime,
      anonymise: false
    };

    this.netscopeApiService.doPostHttpRequest("/parquets_files", payload, Success<any>, Failure<any>).subscribe(async (resp) => {
      if (!(resp instanceof Success)) {
        console.error("could not load the data, the previous data is kept");
        return;
      }

      let parquetBytesAreCompressed: boolean = resp.element.compress_parquets_bytes;

      let bytes: Uint8Array;
      let aggregatedBytes: Uint8Array;
      let threatsBytes: Uint8Array;

      console.log("processing bytes");

      if (! parquetBytesAreCompressed) {
        // Flows
        let bytesContent: string = resp.element.bytes_content;
        let fileBytesContent = atob(bytesContent);
        var bytesFromUncompressed = new Uint8Array(fileBytesContent.length);
        for (var i = 0; i < fileBytesContent.length; i++) {
          bytesFromUncompressed[i] = fileBytesContent.charCodeAt(i);
        }
        bytes = bytesFromUncompressed;
        // Aggregated Flows
        let aggregatedBytesContent: string = resp.element.aggregated_bytes_content;
        let fileAggregatedBytesContent = atob(aggregatedBytesContent);
        var aggregatedBytesFromUncompressed = new Uint8Array(fileAggregatedBytesContent.length);
        for (var i = 0; i < fileAggregatedBytesContent.length; i++) {
          aggregatedBytesFromUncompressed[i] = fileAggregatedBytesContent.charCodeAt(i);
        }
        aggregatedBytes = aggregatedBytesFromUncompressed;
        // Threats
        let threatsBytesContent: string = resp.element.threats_bytes_content;
        let threatsFileBytesContent = atob(threatsBytesContent);
        var threatsBytesFromUncompressed = new Uint8Array(threatsFileBytesContent.length);
        for (var i = 0; i < threatsFileBytesContent.length; i++) {
          threatsBytesFromUncompressed[i] = threatsFileBytesContent.charCodeAt(i);
        }
        threatsBytes = threatsBytesFromUncompressed;
      } else {
        // Flows
        const compressedBytes: string = resp.element.compressed_bytes_content;
        const compressedBytesAsArray = NetscopeUtils.base64ToArrayBuffer(compressedBytes);
        let bytesFromCompressed = pako.ungzip(compressedBytesAsArray, {to: "buffer"});
        bytes = bytesFromCompressed;
        // Aggregated Flows
        const aggregatedBytesContent: string = resp.element.aggregated_bytes_content;
        const compressedAggregatedBytesAsArray = NetscopeUtils.base64ToArrayBuffer(aggregatedBytesContent);
        let aggregatedBytesFromCompressed = pako.ungzip(compressedAggregatedBytesAsArray, {to: "buffer"});
        aggregatedBytes = aggregatedBytesFromCompressed;
        // Threats
        const threatsCompressedBytes: string = resp.element.threats_compressed_bytes_content;
        const threatsCompressedBytesAsArray = NetscopeUtils.base64ToArrayBuffer(threatsCompressedBytes);
        let threatsBytesFromCompressed = pako.ungzip(threatsCompressedBytesAsArray, {to: "buffer"});
        threatsBytes = threatsBytesFromCompressed;
      }
      console.log("done processing bytes");

      let nodeMapping: Map<string, DuckDbResourceObject> = new Map(Object.entries(resp.element.node_mapping));
      let externalIps: Array<DuckDbExternalIp> = resp.element.external_ips.external_ips;
      let appsGroups: Array<DuckDbAppsGroup> = resp.element.apps_groups.apps_groups;

      let selectedTimestamps = resp.element.selected_timestamps;

      let timeselectionLabel = resp.element.timeselection_label;

      const extensionsBaseUrl = window.location.origin + '/assets/parquet_extensions/';
      let parquetExtensionUrl = new URL(extensionsBaseUrl + "parquet.duckdb_extension.wasm").toString();
      let jsonExtensionUrl = new URL(extensionsBaseUrl + "json.duckdb_extension.wasm").toString();
      let inetExtensionUrl = new URL(extensionsBaseUrl + "inet.duckdb_extension.wasm").toString();
      const EXTENSIONS_BUNDLE = {
        extensions: {
          parquetModule: parquetExtensionUrl,
          jsonModule: jsonExtensionUrl,
          inetModule: inetExtensionUrl
        }
      }

      await this.db.registerFileBuffer('file.parquet', bytes)
      await this.db.registerFileBuffer('aggregated_file.parquet', aggregatedBytes)
      await this.db.registerFileBuffer('threats_file.parquet', threatsBytes)


      this.isUsingCdn = false;

      let conn = await this.db.connect();
      let loadParquetExtensionQuery = this.isUsingCdn ? "LOAD PARQUET;" : `LOAD '${EXTENSIONS_BUNDLE.extensions.parquetModule}';`
      let loadJsonExtensionQuery = this.isUsingCdn ? "LOAD JSON;" : `LOAD '${EXTENSIONS_BUNDLE.extensions.jsonModule}';`
      let loadInetExtensionQuery = this.isUsingCdn ? "LOAD INET;" : `LOAD '${EXTENSIONS_BUNDLE.extensions.inetModule}';`

      console.log("loading extensions");

      // await conn.query(loadParquetExtensionQuery);
      // await conn.query(loadJsonExtensionQuery);
      // await conn.query(loadInetExtensionQuery);

      let loadAllExtensionsAtOnce = `${loadParquetExtensionQuery} ${loadJsonExtensionQuery} ${loadInetExtensionQuery}`;
      await conn.query(loadAllExtensionsAtOnce);

      console.log("loaded extensions");

      // await conn.query("select (INET '192.168.0.1') <<= INET '10.0.0.0/24' ");

      // Flows
      await conn.query(`DROP TABLE if exists olap_flows_hourly;`);
      // await conn.query(`CREATE TABLE olap_flows_hourly AS select * from read_parquet('file.parquet')`);

      // Threats
      await conn.query(`DROP TABLE if exists threats;`);
      await conn.query(`CREATE TABLE threats AS
        select
          *
        from read_parquet('threats_file.parquet')
      `);

      conn.close();

      let dbAndNodeMappingUuid = NetscopeUtils.generateUuid();

      let newDatabaseAndNodeMapping: DatabaseAndNodemapping = {
        db: this.db,
        nodeMapping: nodeMapping,
        externalIps: externalIps,
        appsGroups: appsGroups,
        selectedTimestamps: selectedTimestamps,
        timeselectionLabel: timeselectionLabel,
        uuid: dbAndNodeMappingUuid
      }

      this.dbSubject.next(newDatabaseAndNodeMapping);
      this.recomputeDashboardData();
    })
  }

  private async reloadData(timePeriod, startTime=0, endTime=0) {
    const connection = await this.db.connect();
    //Use DuckDB connection to select config from parquet into table
    await this.fetchData(timePeriod, startTime, endTime);
    await connection.close();
  }

  public recomputeDashboardData(): void {
    console.log("recomputing data");

    let comboboxSelectionAsObservable = this.netscopeUiService.comboboxSelectionItemSubject.asObservable();
    let dbAndNodeMappingAsObservable = this.dbSubject.asObservable();

    let combinedObservable = combineLatest([
      comboboxSelectionAsObservable,
      dbAndNodeMappingAsObservable
    ]).pipe(take(1));

    combinedObservable.subscribe(async ([comboboxSelection, dbAndNodeMapping]) => {
      console.log("raw data is ready to be processed");
      let db = dbAndNodeMapping.db;

      // Filters of the top bar
      let baseClause = comboboxSelectionWhereClause(comboboxSelection, dbAndNodeMapping);
      let whereClause = `where ${baseClause}`;

      // TODO: check that current dbSubject is not enough
      let dataUuidAndWhere = `${dbAndNodeMapping.uuid}__${whereClause}`;

      if (this.lastUiRequestUuid == dataUuidAndWhere) {
        return;
      }
      this.lastUiRequestUuid = dataUuidAndWhere;

      console.log("connecting to embedded database");
      const conn = await db.connect();

      let nodeMap: Map<string, FlowRessourceObject> = new Map<string, FlowRessourceObject>();
      let flowsRepository = new FlowsRepositoryWithBrowserDuckDB(conn, whereClause, nodeMap, new Map(), new Map(), dbAndNodeMapping.nodeMapping);
      let asnsCountriesMaps = await flowsRepository.buildAsnsAndCountriesMaps();
      flowsRepository.countryMap = asnsCountriesMaps.countryMap;
      flowsRepository.asnMap = asnsCountriesMaps.asnMap;

      let allFlowsRepository = flowsRepository.clone();
      await flowsRepository.init(whereClause);

      // To help component to see if they are processing several times the same data
      let dashboardDataUuid = uuid();

      let duckDbDataResult: DuckDbDashboardData = {
        allFlowsRepository: allFlowsRepository,
        flowsRepository: flowsRepository,

        nodeMapping: dbAndNodeMapping.nodeMapping,
        externalIps: dbAndNodeMapping.externalIps,
        appsGroups: dbAndNodeMapping.appsGroups,
        selectedTimestamps: dbAndNodeMapping.selectedTimestamps,
        timeSelectionLabel: dbAndNodeMapping.timeselectionLabel,

        dashboardDataUuid: dashboardDataUuid,
        dataUuid: dbAndNodeMapping.uuid,
        dataUuidAndWhere: dataUuidAndWhere
      }
      console.log("sending duckdb data to observer");
      this.dashboardDataSubject.next(duckDbDataResult);
      this.netscopeUiService.duckDbDataSubject.next(duckDbDataResult);
    });

  }
}

@Injectable({
  providedIn: 'root'
})
export class DuckDbServiceWithRemoteDuckDb implements DuckdbServiceInterface {

  public dbSubject: ReplaySubject<DatabaseAndNodemapping> = new ReplaySubject(1);
  public dashboardDataSubject: ReplaySubject<DuckDbDashboardData> = new ReplaySubject(1);
  public lastUiRequestUuid:string = "";

  // Loads (time selections) and recomputations (filters) run concurrently, and may complete in any order: each one
  // gets a number, and only the result of the latest one is published, so that the UI never goes back to an older
  // time selection or filter.
  private lastLoadNumber = 0;
  private lastRecomputeNumber = 0;

  constructor(private netscopeApiService: NetscopeApiService, private netscopeUiService: NetscopeUiService) {}

  async requestLoadingOfData(timePeriod, startTime=0, endTime=0): Promise<void> {
    if (timePeriod === undefined) {
      timePeriod = "last 1day"
    }

    let payload = {
      timePeriod: timePeriod,
      startTime: startTime,
      endTime: endTime,
      anonymise: false,
      serverSide: true
    };

    const loadNumber = ++this.lastLoadNumber;

    this.netscopeApiService.doPostHttpRequest("/parquets_files", payload, Success<any>, Failure<any>).subscribe(async (resp) => {
      if (loadNumber !== this.lastLoadNumber) {
        console.log(`ignoring the data of load #${loadNumber}: load #${this.lastLoadNumber} has been requested since`);
        return;
      }

      if (!(resp instanceof Success)) {
        // The API service has already raised the error state: the data of the previous load is kept
        console.error(`load #${loadNumber} failed, the previous data is kept`);
        this.netscopeUiService.setLoadingState(false, "main");
        return;
      }

      let selectedTimestamps = resp.element.selected_timestamps;
      let timeselectionLabel = resp.element.timeselection_label;
      let nodeMapping: Map<string, DuckDbResourceObject> = new Map(Object.entries(resp.element.node_mapping));
      let externalIps: Array<DuckDbExternalIp> = resp.element.external_ips.external_ips;
      let appsGroups: Array<DuckDbAppsGroup> = resp.element.apps_groups.apps_groups;

      let aggregated_tmp_parquet_filepath = resp.element.aggregated_tmp_parquet_filepath
      let tmp_parquet_filepath = resp.element.tmp_parquet_filepath

      let dbAndNodeMappingUuid = NetscopeUtils.generateUuid();

      let newDatabaseAndNodeMapping: DatabaseAndNodemapping = {
        nodeMapping: nodeMapping,
        externalIps: externalIps,
        appsGroups: appsGroups,
        selectedTimestamps: selectedTimestamps,
        timeselectionLabel: timeselectionLabel,
        uuid: dbAndNodeMappingUuid,
        aggregated_tmp_parquet_filepath: aggregated_tmp_parquet_filepath,
        tmp_parquet_filepath: tmp_parquet_filepath
      }

      this.dbSubject.next(newDatabaseAndNodeMapping);
      this.recomputeDashboardData();
    });

    this.recomputeDashboardData();
  }

  recomputeDashboardData(): void {
    console.log("recomputing data");

    let comboboxSelectionAsObservable = this.netscopeUiService.comboboxSelectionItemSubject.asObservable();
    let dbAndNodeMappingAsObservable = this.dbSubject.asObservable();

    let combinedObservable = combineLatest([
      comboboxSelectionAsObservable,
      dbAndNodeMappingAsObservable
    ]).pipe(take(1));

    combinedObservable.subscribe(async ([comboboxSelection, dbAndNodeMapping]) => {
      console.log("raw data is ready to be processed");

      // Filters of the top bar
      let baseClause = comboboxSelectionWhereClause(comboboxSelection, dbAndNodeMapping);
      let whereClause = `where ${baseClause}`;

      // TODO: check that current dbSubject is not enough
      let dataUuidAndWhere = `${dbAndNodeMapping.uuid}__${whereClause}`;

      if (this.lastUiRequestUuid == dataUuidAndWhere) {
        return;
      }
      this.lastUiRequestUuid = dataUuidAndWhere;
      const recomputeNumber = ++this.lastRecomputeNumber;

      console.log("connecting to embedded database");
      // const conn = await db.connect();

      let nodeMap: Map<string, FlowRessourceObject> = new Map<string, FlowRessourceObject>();
      let flowsRepository = new FlowsRepositoryWithRemoteDuckDB(this.netscopeApiService, null, whereClause, nodeMap, new Map(), new Map(), dbAndNodeMapping.nodeMapping, dbAndNodeMapping.tmp_parquet_filepath, dbAndNodeMapping.aggregated_tmp_parquet_filepath);
      flowsRepository.flows_files = dbAndNodeMapping.tmp_parquet_filepath;
      flowsRepository.aggregated_flows_files = dbAndNodeMapping.aggregated_tmp_parquet_filepath;

      let asnsCountriesMaps = await flowsRepository.buildAsnsAndCountriesMaps();
      flowsRepository.countryMap = asnsCountriesMaps.countryMap;
      flowsRepository.asnMap = asnsCountriesMaps.asnMap;

      let allFlowsRepository = flowsRepository.clone();
      await flowsRepository.init(whereClause);

      if (recomputeNumber !== this.lastRecomputeNumber) {
        console.log(`ignoring recomputation #${recomputeNumber}: recomputation #${this.lastRecomputeNumber} has been requested since`);
        return;
      }

      // To help component to see if they are processing several times the same data
      let dashboardDataUuid = uuid();

      let duckDbDataResult: DuckDbDashboardData = {
        allFlowsRepository: allFlowsRepository,
        flowsRepository: flowsRepository,

        nodeMapping: dbAndNodeMapping.nodeMapping,
        externalIps: dbAndNodeMapping.externalIps,
        appsGroups: dbAndNodeMapping.appsGroups,
        selectedTimestamps: dbAndNodeMapping.selectedTimestamps,
        timeSelectionLabel: dbAndNodeMapping.timeselectionLabel,

        dashboardDataUuid: dashboardDataUuid,
        dataUuid: dbAndNodeMapping.uuid,
        dataUuidAndWhere: dataUuidAndWhere
      }
      console.log("sending duckdb data to observer");
      this.dashboardDataSubject.next(duckDbDataResult);
      this.netscopeUiService.duckDbDataSubject.next(duckDbDataResult);
    });
  }
}
